var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { DateRangeSelector, Dropdown, TextInput } from "ee-react-components";
import React, { useState } from "react";
import { IncidentIssue, } from "ee-nestjs-interfaces";
import { charger } from "ee-proto-interfaces";
import { PaginatedTable } from "ee-react-components";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import { ISSUE_LABELS, issueLabel } from "../Incidents/IncidentLabels";
import { TicketLink } from "../Incidents/TicketLink";
export var OutagesTable = function (_a) {
    var resourceUrl = _a.resourceUrl, buildRowLink = _a.buildRowLink, maxRows = _a.maxRows, refreshInterval = _a.refreshInterval;
    var rowsFromResponse = function (response) { return response.outages; };
    var rowCountFromResponse = function (response) { return response.pages; };
    var columns = [
        {
            header: "Charger Serial Number",
            accessorFn: function (row) {
                return row.serialNumber;
            },
        },
        {
            header: "Outage Start Time",
            accessorFn: function (row) {
                return new Date(row.start).toLocaleString();
            },
        },
        {
            header: "Outage Duration",
            accessorFn: function (row) {
                var totalSeconds = Math.floor((new Date(row.end).getTime() - new Date(row.start).getTime()) / 1000);
                if (totalSeconds <= 0) {
                    return "0h 0m 0s";
                }
                var seconds = totalSeconds % 60;
                var totalMinutes = Math.floor(totalSeconds / 60);
                var minutes = totalMinutes % 60;
                var hours = Math.floor(totalMinutes / 60);
                return "".concat(hours, "h ").concat(minutes, "m ").concat(seconds, "s");
            },
        },
        {
            header: "Unavailable Reason[s]",
            accessorFn: function (row) {
                if (!row.unavailableReasons || row.unavailableReasons.length == 0)
                    return "";
                return row.unavailableReasons
                    .map(function (ur) { return charger.unavailableReasonToJSON(ur); })
                    .join(", ");
            },
        },
        // What the outage is attributed to, read from the incidents it overlapped
        // rather than set on the outage itself. An outage with none is one nobody
        // has explained yet, which the "no incidents" filter exists to find.
        {
            header: "Incident Issue[s]",
            accessorFn: function (row) {
                return row.issues.map(issueLabel).join(", ");
            },
        },
        {
            header: "Incidents",
            accessorFn: function (row) {
                return row.incidents.map(function (incident) { return incident.opsTicket; }).join(", ");
            },
            // Each ticket as its own link to Jira; the row still navigates to the
            // outage everywhere else in the cell.
            cell: function (info) { return (React.createElement("span", { className: "flex flex-row flex-wrap gap-x-[8px]" }, info.row.original.incidents.length === 0
                ? "-"
                : info.row.original.incidents.map(function (incident) { return (React.createElement(TicketLink, { key: incident.uid, ticket: incident.opsTicket })); }))); },
        },
    ];
    return (React.createElement(PaginatedTable, { buildRowLink: buildRowLink, baseUrl: resourceUrl, itemsPerPage: maxRows !== null && maxRows !== void 0 ? maxRows : 10, rowsFromResponse: rowsFromResponse, rowCountFromResponse: rowCountFromResponse, columnDefinitions: columns, refreshInterval: refreshInterval }));
};
export var Outages = function (_a) {
    var resourceUrlBase = _a.resourceUrlBase, siteId = _a.siteId, earliestStatus = _a.earliestStatus, chargerDetails = _a.chargerDetails;
    var _b = useState([
        new Date(),
        new Date(),
    ]), dateRange = _b[0], setDateRange = _b[1];
    var _c = useState(1.0), filterSessionsUnder = _c[0], setFilterSessionsUnder = _c[1];
    var chargerOptions = __spreadArray([
        {
            name: "All chargers",
            value: "ALL",
            selectedLabel: "All chargers",
        }
    ], chargerDetails
        .slice()
        .sort(function (a, b) { return a.ocppId.localeCompare(b.ocppId); }) // sort alphabetically
        .map(function (charger) { return ({
        name: charger.ocppId,
        value: String(charger.id),
    }); }), true);
    var _d = useState("ALL"), sortState = _d[0], setSortState = _d[1];
    var unavailableReasonOptions = __spreadArray([
        {
            name: "All",
            value: undefined,
            selectedLabel: "All reasons",
        }
    ], Object.entries(charger.UnavailableReason)
        .filter(function (_a) {
        var value = _a[1];
        return value !== charger.UnavailableReason.NONE && typeof value === "number";
    })
        .map(function (_a) {
        var key = _a[0], value = _a[1];
        return ({
            name: key,
            value: value,
        });
    }), true);
    var _e = useState(undefined), unavailableReason = _e[0], setUnavailableReason = _e[1];
    // The empty value is what MultiSelectDropdown shows when nothing is picked,
    // and picking it clears the selection back to "no issue filter".
    var issueOptions = __spreadArray([
        { name: "All issues", value: "" }
    ], Object.values(IncidentIssue).map(function (value) { return ({
        name: ISSUE_LABELS[value],
        value: value,
    }); }), true);
    var _f = useState([]), issues = _f[0], setIssues = _f[1];
    var _g = useState(false), includeWithoutIncidents = _g[0], setIncludeWithoutIncidents = _g[1];
    var apiSearchParams = new URLSearchParams();
    if (filterSessionsUnder) {
        apiSearchParams.append("minsFilter", filterSessionsUnder);
    }
    if (sortState != "ALL") {
        apiSearchParams.append("chargerId", sortState);
    }
    if (unavailableReason) {
        apiSearchParams.append("unavailableReason", unavailableReason);
    }
    // Repeated rather than comma-joined, which the endpoint also accepts: this is
    // what a URL built by hand or by a form would look like.
    issues.forEach(function (issue) { return apiSearchParams.append("issue", issue); });
    if (includeWithoutIncidents) {
        apiSearchParams.append("includeWithoutIncidents", "true");
    }
    return (React.createElement("div", { className: "flex flex-col gap-y-[32px]" },
        React.createElement(DateRangeSelector, { dateRange: dateRange, setDateRange: setDateRange, firstDate: new Date(earliestStatus) }),
        React.createElement("div", { className: "flex flex-row gap-x-[32px] items-center flex-wrap gap-y-[16px]" },
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "SORT BY CHARGER"),
                React.createElement("div", { className: "flex flex-row gap-x-[32px]" },
                    React.createElement("span", { className: "relative w-full" },
                        React.createElement(Dropdown, { items: chargerOptions, onClick: setSortState, currentSelection: sortState })))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "FILTER OUT SESSIONS UNDER"),
                React.createElement("div", { className: "flex flex-row gap-x-[32px]" },
                    React.createElement("span", { className: "relative after:content-['mins'] after:absolute after:translate-y-[-50%] after:top-[50%] after:right-[8px] after:text-black-600" },
                        React.createElement(TextInput, { placeholder: filterSessionsUnder, value: filterSessionsUnder, required: false, type: "number", step: "1", min: "0", pattern: "^\\d+(\\.)\\d{2}$", className: "!pr-[48px]", onChange: function (e) { return setFilterSessionsUnder(e.target.value); }, disabled: false })))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "FILTER BY UNAVAILABLE REASON"),
                React.createElement("div", { className: "flex flex-row gap-x-[32px]" },
                    React.createElement("span", { className: "relative w-full" },
                        React.createElement(Dropdown, { items: unavailableReasonOptions, onClick: setUnavailableReason, currentSelection: unavailableReason })))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "FILTER BY INCIDENT ISSUE"),
                React.createElement("div", { className: "flex flex-row gap-x-[32px]" },
                    React.createElement("span", { className: "relative w-full" },
                        React.createElement(MultiSelectDropdown, { items: issueOptions, currentSelections: issues, onChange: setIssues })))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "OUTAGES WITHOUT INCIDENTS"),
                React.createElement("label", { className: "flex flex-row gap-x-2 items-center h-[40px] cursor-pointer" },
                    React.createElement("input", { type: "checkbox", className: "w-[20px] h-[20px] accent-green", checked: includeWithoutIncidents, onChange: function (e) { return setIncludeWithoutIncidents(e.target.checked); } }),
                    React.createElement("span", { className: "font-N8 text-white" }, "Include")))),
        React.createElement("div", { className: "flex flex-col w-fit gap-y-4" },
            React.createElement(OutagesTable, { buildRowLink: function (row) {
                    return "/outage/".concat(siteId, "/").concat(row.chargerId, "/").concat(row.start);
                }, resourceUrl: "".concat(resourceUrlBase, "/").concat(dateRange[0].toISOString(), "/").concat(dateRange[1].toISOString(), "?").concat(apiSearchParams.toString()), maxRows: 20, refreshInterval: 0 }))));
};
